/** @jsxImportSource react */
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

interface HomePageContextType {
  isHomePage: boolean;
  isModalOpen: boolean;
  setModalOpen: (isOpen: boolean) => void;
}

const HomePageContext = createContext<HomePageContextType>({
  isHomePage: false,
  isModalOpen: false,
  setModalOpen: () => {},
});

export const useHomePage = () => {
  const context = useContext(HomePageContext);
  if (!context) {
    throw new Error('useHomePage must be used within a HomePageProvider');
  }
  return context;
};

interface HomePageProviderProps {
  children: React.ReactNode;
}

export const HomePageProvider: React.FC<HomePageProviderProps> = ({ children }) => {
  const location = useLocation();
  const [isHomePage, setIsHomePage] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setIsHomePage(location.pathname === '/');
  }, [location.pathname]);

  const value = useMemo<HomePageContextType>(
    () => ({ isHomePage, isModalOpen, setModalOpen: setIsModalOpen }),
    [isHomePage, isModalOpen],
  );

  return (
    <HomePageContext.Provider value={value}>
      {children}
    </HomePageContext.Provider>
  );
};
